import { useMemo, useState } from 'react';
import { Copy, Mail, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import type { AlwaysdataMailbox } from '@jeraisonne/db';
import { mailboxAddress, mailboxCreateSchema, mailboxUpdateSchema } from '@jeraisonne/db';
import {
  AlwaysdataError,
  useAlwaysdataConfig,
  useAlwaysdataMailboxes,
  useCreateMailbox,
  useDeleteMailbox,
  useUpdateMailbox,
} from '@/lib/admin/alwaysdata';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';
const checkboxCls = 'accent-primary-500 h-4 w-4';

/**
 * Panneau « Redirections » : liste les boîtes @<domain>, permet d'en créer,
 * modifier redirect_*, et supprimer. Portée volontaire : redirect_enabled /
 * redirect_to / redirect_local_copy / redirect_srs (+ annotation).
 */
export function AlwaysdataMailboxesPanel() {
  const { data: config } = useAlwaysdataConfig();
  const enabled = !!config?.token_set;
  const q = useAlwaysdataMailboxes(enabled);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const filtered = useMemo(() => {
    if (!q.data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return q.data.mailboxes;
    return q.data.mailboxes.filter(
      (m) =>
        m.name.toLowerCase().includes(needle) ||
        (m.redirect_to ?? '').toLowerCase().includes(needle) ||
        (m.annotation ?? '').toLowerCase().includes(needle),
    );
  }, [q.data, search]);

  if (!enabled) {
    return (
      <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
        <Mail className="text-ink-300 mx-auto mb-3 h-8 w-8" />
        <p className="font-display text-ink-700 text-[14px] font-semibold">Aucun token configuré</p>
        <p className="text-ink-500 mt-1 font-sans text-[13px]">
          Renseigne d'abord le token API dans l'onglet <b>Connexion API</b>.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          className={`${inputCls} flex-1`}
          placeholder="Rechercher (nom, cible, note)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="flex items-center gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${q.isFetching ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setShowCreate((v) => !v)}
            className="flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {showCreate ? 'Annuler' : 'Nouvelle redirection'}
          </Button>
        </div>
      </div>

      {showCreate && q.data && (
        <CreateMailboxForm domainName={q.data.domain.name} onDone={() => setShowCreate(false)} />
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : q.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 font-sans text-[13px] text-red-800">
          {q.error instanceof AlwaysdataError ? q.error.message : String(q.error)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
          <Mail className="text-ink-300 mx-auto mb-3 h-8 w-8" />
          <p className="font-display text-ink-600 font-semibold">Aucune boîte</p>
          <p className="text-ink-500 mt-1 font-sans text-[13px]">
            {search
              ? 'Aucune boîte ne correspond à cette recherche.'
              : `Le domaine ${q.data?.domain.name} n'a pas encore de boîte email.`}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {filtered.map((m) => (
            <MailboxRow key={m.id} mailbox={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function CreateMailboxForm({ domainName, onDone }: { domainName: string; onDone: () => void }) {
  const create = useCreateMailbox();
  const [name, setName] = useState('');
  const [redirectTo, setRedirectTo] = useState('');
  const [localCopy, setLocalCopy] = useState(false);
  const [srs, setSrs] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = mailboxCreateSchema.safeParse({
      name,
      redirect_to: redirectTo,
      redirect_enabled: true,
      redirect_local_copy: localCopy,
      redirect_srs: srs,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'validation invalide');
      return;
    }
    try {
      await create.mutateAsync(parsed.data);
      onDone();
    } catch (err) {
      setError(err instanceof AlwaysdataError ? err.message : String(err));
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border-primary-200 bg-primary-50/40 flex flex-col gap-3 rounded-2xl border p-4"
    >
      <p className="font-display text-ink-800 text-[14px] font-bold">Nouvelle redirection</p>

      <div className="flex flex-col gap-1">
        <label className={labelCls} htmlFor="new-mb-name">
          Partie locale
        </label>
        <div className="flex items-center gap-2">
          <input
            id="new-mb-name"
            className={`${inputCls} flex-1`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="parent1"
            autoComplete="off"
          />
          <span className="text-ink-500 font-sans text-[13px]">@{domainName}</span>
        </div>
        <p className="text-ink-500 font-sans text-[12px]">
          Astuce : le wildcard <code>*</code> crée un catch-all pour tout le domaine.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label className={labelCls} htmlFor="new-mb-to">
          Redirigée vers
        </label>
        <textarea
          id="new-mb-to"
          className={`${inputCls} min-h-[4.5rem] resize-y`}
          value={redirectTo}
          onChange={(e) => setRedirectTo(e.target.value)}
          placeholder="destination@example.com&#10;autre@example.com"
        />
        <p className="text-ink-500 font-sans text-[12px]">
          Une adresse par ligne (ou séparées par un espace / une virgule).
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 font-sans text-[13px]">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={localCopy}
            onChange={(e) => setLocalCopy(e.target.checked)}
          />
          Garder une copie locale
        </label>
        <label className="flex items-center gap-2 font-sans text-[13px]">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={srs}
            onChange={(e) => setSrs(e.target.checked)}
          />
          Activer SRS (recommandé)
        </label>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-2.5 font-sans text-[12px] text-red-800">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? 'Création…' : 'Créer la redirection'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Annuler
        </Button>
      </div>
    </form>
  );
}

function MailboxRow({ mailbox }: { mailbox: AlwaysdataMailbox }) {
  const update = useUpdateMailbox();
  const del = useDeleteMailbox();

  const [editing, setEditing] = useState(false);
  const [redirectTo, setRedirectTo] = useState(mailbox.redirect_to);
  const [enabled, setEnabled] = useState(mailbox.redirect_enabled);
  const [localCopy, setLocalCopy] = useState(mailbox.redirect_local_copy);
  const [srs, setSrs] = useState(mailbox.redirect_srs);
  const [error, setError] = useState<string | null>(null);

  const address = mailboxAddress(mailbox);
  const busy = update.isPending || del.isPending;

  async function save() {
    setError(null);
    const parsed = mailboxUpdateSchema.safeParse({
      redirect_to: redirectTo,
      redirect_enabled: enabled,
      redirect_local_copy: localCopy,
      redirect_srs: srs,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'validation invalide');
      return;
    }
    try {
      await update.mutateAsync({ id: mailbox.id, ...parsed.data });
      setEditing(false);
    } catch (err) {
      setError(err instanceof AlwaysdataError ? err.message : String(err));
    }
  }

  async function toggle() {
    try {
      await update.mutateAsync({ id: mailbox.id, redirect_enabled: !mailbox.redirect_enabled });
    } catch (err) {
      window.alert(err instanceof AlwaysdataError ? err.message : String(err));
    }
  }

  async function remove() {
    if (!window.confirm(`Supprimer la boîte ${address} ?`)) return;
    try {
      await del.mutateAsync(mailbox.id);
    } catch (err) {
      window.alert(err instanceof AlwaysdataError ? err.message : String(err));
    }
  }

  async function copyAddr() {
    try {
      await navigator.clipboard.writeText(address);
    } catch {
      /* pas de clipboard = pas grave */
    }
  }

  const targets = mailbox.redirect_to
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-3.5 sm:p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 flex flex-wrap items-center gap-2 text-[15px] font-semibold">
            {address}
            <button
              type="button"
              aria-label="Copier l'adresse"
              onClick={copyAddr}
              className="text-ink-400 hover:text-primary-600"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
            {!mailbox.redirect_enabled && (
              <span className="bg-ink-100 text-ink-500 rounded-full px-2 py-0.5 text-[11px] font-medium">
                Désactivée
              </span>
            )}
            {mailbox.redirect_local_copy && (
              <span className="bg-primary-50 text-primary-700 rounded-full px-2 py-0.5 text-[11px] font-medium">
                Copie locale
              </span>
            )}
            {mailbox.name === '*' && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                Catch-all
              </span>
            )}
          </p>
          {!editing && (
            <p className="text-ink-500 mt-1 font-sans text-[13px]">
              {targets.length ? (
                <>→ {targets.join(', ')}</>
              ) : (
                <span className="italic">aucune destination</span>
              )}
            </p>
          )}
        </div>

        {!editing && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label={mailbox.redirect_enabled ? 'Désactiver' : 'Activer'}
              onClick={toggle}
              disabled={busy}
              className={`rounded-lg px-2 py-1 font-sans text-[11px] font-semibold transition-colors ${
                mailbox.redirect_enabled
                  ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
              }`}
            >
              {mailbox.redirect_enabled ? 'ON' : 'OFF'}
            </button>
            <button
              type="button"
              aria-label="Modifier"
              onClick={() => setEditing(true)}
              disabled={busy}
              className="text-ink-400 hover:text-primary-600 p-1.5 disabled:opacity-40"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Supprimer"
              onClick={remove}
              disabled={busy}
              className="text-ink-400 p-1.5 hover:text-red-600 disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className={labelCls} htmlFor={`edit-to-${mailbox.id}`}>
              Redirigée vers
            </label>
            <textarea
              id={`edit-to-${mailbox.id}`}
              className={`${inputCls} min-h-[4.5rem] resize-y`}
              value={redirectTo}
              onChange={(e) => setRedirectTo(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 font-sans text-[13px]">
              <input
                type="checkbox"
                className={checkboxCls}
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Redirection active
            </label>
            <label className="flex items-center gap-2 font-sans text-[13px]">
              <input
                type="checkbox"
                className={checkboxCls}
                checked={localCopy}
                onChange={(e) => setLocalCopy(e.target.checked)}
              />
              Copie locale
            </label>
            <label className="flex items-center gap-2 font-sans text-[13px]">
              <input
                type="checkbox"
                className={checkboxCls}
                checked={srs}
                onChange={(e) => setSrs(e.target.checked)}
              />
              SRS
            </label>
          </div>
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-2.5 font-sans text-[12px] text-red-800">
              {error}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={busy}>
              {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setRedirectTo(mailbox.redirect_to);
                setEnabled(mailbox.redirect_enabled);
                setLocalCopy(mailbox.redirect_local_copy);
                setSrs(mailbox.redirect_srs);
                setEditing(false);
                setError(null);
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
